import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { DataCacheProvider } from './contexts/DataCacheContext';
import { ToastProvider } from './contexts/ToastContext';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import Login from './components/Login';
import Register from './components/Register';
import CoachDashboard from './components/CoachDashboard';
import AthleteDashboard from './components/AthleteDashboard';
import ProgramEditor from './components/ProgramEditor';
import AthleteProgram from './components/AthleteProgram';
import WorkoutExecution from './components/WorkoutExecution';
import AthleteHistory from './components/AthleteHistory';
import WorkoutLogDetail from './components/WorkoutLogDetail';
import AthleteMonitoring from './components/AthleteMonitoring';
import AthleteProgress from './components/AthleteProgress';
import ExerciseProgress from './components/ExerciseProgress';
import ExerciseCatalog from './components/ExerciseCatalog';
import ResetPassword from './components/ResetPassword';
import ConsentGate from './components/ConsentGate';
import PrivacyPolicy from './components/legal/PrivacyPolicy';
import TermsOfService from './components/legal/TermsOfService';
import AthletePrograms from './components/AthletePrograms';
import CreateProgram from './components/CreateProgram';
import AdminDashboard from './components/AdminDashboard';
import FreeWorkout from './components/FreeWorkout';
import ProfilePage from './components/ProfilePage';
import CommunityPage from './components/community/CommunityPage';
import PublicProfilePage from './components/community/PublicProfilePage';
import SessionExpiredHandler from './components/SessionExpiredHandler';
import { captureFirstTouch } from './lib/attribution';

function App() {
  // Prima attribuzione UTM (first-touch): va catturata a ogni ingresso
  // nell'app, qualunque sia la pagina di atterraggio.
  useEffect(() => {
    captureFirstTouch();
  }, []);

  // Disable pull-to-refresh on mobile (especially iOS Safari)
  useEffect(() => {
    let startY = 0;
    let startScrollTop = 0;

    const handleTouchStart = (e) => {
      startY = e.touches[0].clientY;
      startScrollTop = window.scrollY || document.documentElement.scrollTop;
    };

    const handleTouchMove = (e) => {
      const touchY = e.touches[0].clientY;
      const touchYDelta = touchY - startY;
      const currentScrollTop = window.scrollY || document.documentElement.scrollTop;

      // Prevent pull-to-refresh: if we started near top OR are currently near top
      // and user is pulling down (touchYDelta > 0)
      if (touchYDelta > 0 && (startScrollTop <= 5 || currentScrollTop <= 5)) {
        // Check if we're inside a scrollable container that's NOT at its top
        let element = e.target;
        let canScroll = false;

        while (element && element !== document.body && element !== document.documentElement) {
          const style = window.getComputedStyle(element);
          const isScrollable = (
            (style.overflowY === 'auto' || style.overflowY === 'scroll') &&
            element.scrollHeight > element.clientHeight
          );

          if (isScrollable && element.scrollTop > 0) {
            // Inside a scrollable element that's not at top, allow scroll
            canScroll = true;
            break;
          }
          element = element.parentElement;
        }

        if (!canScroll) {
          // Prevent pull-to-refresh
          e.preventDefault();
        }
      }
    };

    // Use non-passive listener to allow preventDefault
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });

    // Also prevent overscroll on the document/body level
    const preventOverscroll = (e) => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      if (scrollTop <= 0) {
        // At top, prevent any upward overscroll
        document.body.style.overscrollBehavior = 'none';
      } else {
        document.body.style.overscrollBehavior = 'contain';
      }
    };

    window.addEventListener('scroll', preventOverscroll, { passive: true });
    preventOverscroll(); // Initial call

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('scroll', preventOverscroll);
    };
  }, []);

  return (
    <ErrorBoundary>
    <Router>
      <ToastProvider>
      <DataCacheProvider>
      <AuthProvider>
        <SessionExpiredHandler />
        <div className="App">
          <Routes>
            <Route path="/" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/consenso" element={<ConsentGate />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/termini" element={<TermsOfService />} />
            <Route
              path="/admin"
              element={
                <ProtectedRoute requireRole="admin">
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/coach"
              element={
                <ProtectedRoute requireCoach>
                  <CoachDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/program/:programId"
              element={
                <ProtectedRoute requireCoach>
                  <ErrorBoundary
                    title="Errore nell'editor del programma"
                    message="Si è verificato un errore durante la modifica del programma. Prova a ricaricare."
                  >
                    <ProgramEditor />
                  </ErrorBoundary>
                </ProtectedRoute>
              }
            />
                        <Route
              path="/coach/athlete/:athleteId"
              element={
                <ProtectedRoute requireCoach>
                  <AthleteMonitoring />
                </ProtectedRoute>
              }
            />
            <Route
              path="/coach/athlete/:athleteId/workout/:workoutLogId"
              element={
                <ProtectedRoute requireCoach>
                  <WorkoutLogDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/athlete"
              element={
                <ProtectedRoute>
                  <AthleteDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/athlete/program"
              element={
                <ProtectedRoute>
                  <AthleteProgram />
                </ProtectedRoute>
              }
            />
            <Route
              path="/athlete/workout/:workoutId"
              element={
                <ProtectedRoute>
                  <ErrorBoundary
                    title="Errore durante l'allenamento"
                    message="Si è verificato un errore. I tuoi progressi sono salvati automaticamente. Prova a ricaricare."
                  >
                    <WorkoutExecution />
                  </ErrorBoundary>
                </ProtectedRoute>
              }
            />
            <Route
              path="/athlete/free-workout"
              element={
                <ProtectedRoute>
                  <ErrorBoundary
                    title="Errore durante l'allenamento libero"
                    message="Si è verificato un errore. I tuoi progressi sono salvati automaticamente. Prova a ricaricare."
                  >
                    <FreeWorkout />
                  </ErrorBoundary>
                </ProtectedRoute>
              }
            />
            <Route
              path="/athlete/history"
              element={
                <ProtectedRoute>
                  <AthleteHistory />
                </ProtectedRoute>
              }
            />
            <Route
              path="/athlete/history/:workoutLogId"
              element={
                <ProtectedRoute>
                  <WorkoutLogDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/athlete/progress"
              element={
                <ProtectedRoute>
                  <AthleteProgress />
                </ProtectedRoute>
              }
            />
            <Route
              path="/athlete/progress/:exerciseId"
              element={
                <ProtectedRoute>
                  <ExerciseProgress />
                </ProtectedRoute>
              }
            />
            <Route
              path="/athlete/catalog"
              element={
                <ProtectedRoute>
                  <ExerciseCatalog />
                </ProtectedRoute>
              }
            />
            <Route
              path="/athlete/programs"
              element={
                <ProtectedRoute>
                  <AthletePrograms />
                </ProtectedRoute>
              }
            />
            <Route
              path="/athlete/profile"
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/athlete/community"
              element={
                <ProtectedRoute>
                  <CommunityPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/athlete/community/:username"
              element={
                <ProtectedRoute>
                  <PublicProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/athlete/programs/new"
              element={
                <ProtectedRoute>
                  <CreateProgram />
                </ProtectedRoute>
              }
            />
            <Route
              path="/athlete/programs/:programId/edit"
              element={
                <ProtectedRoute>
                  <ErrorBoundary
                    title="Errore nell'editor del programma"
                    message="Si è verificato un errore durante la modifica del programma. Prova a ricaricare."
                  >
                    <ProgramEditor />
                  </ErrorBoundary>
                </ProtectedRoute>
              }
            />
            <Route
              path="/athlete/programs/:programId/view"
              element={
                <ProtectedRoute>
                  <AthleteProgram />
                </ProtectedRoute>
              }
            />
          </Routes>
        </div>
      </AuthProvider>
      </DataCacheProvider>
      </ToastProvider>
    </Router>
    </ErrorBoundary>
  );
}

export default App;
